import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { MdChevronLeft, MdClose, MdOutlineEdit, MdOutlineRefresh } from 'react-icons/md';
import { FormSelect, SlideModal, useToast } from '@podus/ui';
import StatsCard from '@/components/StatsCard';
import StatusBadge from '@/components/StatusBadge';
import ViewButton from '@/components/ViewButton';
import CaseViewModal, { type CaseRow } from '@/components/CaseViewModal';
import ConfirmActionModal from '@/components/ConfirmActionModal';
import { DataTable } from '@podus/ui';
import type { Column } from '@podus/ui';
import { api } from '@/lib/api';
import { exportCSV } from '@/lib/csv';
import type { StaffMember, Case, CaseStats } from '@/types';

/* ─── Helpers ─── */

function mapToRow(c: Case, index: number): CaseRow {
    const applicant = c.parties?.find((p) => p.role === 'Applicant' || p.role === 'Claimant');
    return {
        id: c.id,
        sn: index + 1,
        date: new Date(c.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
        complainantName: applicant?.name ?? c.caseNumber,
        caseCategory: c.caseCategory,
        disputeCategory: c.disputeCategory,
        evidence: c.evidence?.length ?? 'Nil',
        amountPaid: `₦${Number(c.amountPaid).toLocaleString()}`,
        status: c.status,
    };
}

const TABS = ['All', 'New', 'Assigned', 'In Progress', 'Resolved'];

const ROLE_OPTIONS = [
    { value: 'Admin', label: 'Admin' },
    { value: 'Case Manager', label: 'Case Manager' },
    { value: 'Mediator', label: 'Mediator' },
];

/* ─── Component ─── */

export default function InternalUserView() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [staff, setStaff] = useState<StaffMember | null>(null);
    const [cases, setCases] = useState<CaseRow[]>([]);
    const [stats, setStats] = useState<CaseStats | null>(null);
    const [activeTab, setActiveTab] = useState('All');
    const [selectedCase, setSelectedCase] = useState<CaseRow | null>(null);
    const [blockModalOpen, setBlockModalOpen] = useState(false);
    const [resendModalOpen, setResendModalOpen] = useState(false);
    const [roleModalOpen, setRoleModalOpen] = useState(false);
    const [draftRole, setDraftRole] = useState<string>('');
    const [savingRole, setSavingRole] = useState(false);
    const toast = useToast();

    useEffect(() => {
        if (!id) return;
        api.get<StaffMember>(`/api/staff/${id}`)
            .then((res) => setStaff(res.response.data ?? null))
            .catch(() => {});
    }, [id]);

    // Mediators are assigned via `assignedMediatorId`; everyone else via
    // `assignedCaseManagerId`. Wait for the role to load before fetching.
    const assignmentParam =
        staff?.role === 'Mediator' ? `assignedMediatorId=${id}` : `assignedCaseManagerId=${id}`;

    useEffect(() => {
        if (!id || !staff) return;
        api.get<CaseStats>(`/api/cases/stats?${assignmentParam}`)
            .then((res) => setStats(res.response.data ?? null))
            .catch(() => {});
    }, [id, staff, assignmentParam]);

    useEffect(() => {
        if (!id || !staff) return;
        const statusParam = activeTab !== 'All' ? `&status=${encodeURIComponent(activeTab)}` : '';
        api.get<Case[]>(`/api/cases?${assignmentParam}${statusParam}&limit=100`)
            .then((res) => setCases((res.response.data ?? []).map(mapToRow)))
            .catch(() => setCases([]));
    }, [id, staff, assignmentParam, activeTab]);

    const handleBlockConfirm = async () => {
        if (!id || !staff) return;
        await api.patch(`/api/staff/${id}/block`, {});
        setStaff((prev) => (prev ? { ...prev, status: prev.status === 'blocked' ? 'active' : 'blocked' } : prev));
    };

    const handleResendConfirm = async () => {
        if (!id) return;
        await api.post(`/api/staff/${id}/resend-invitation`, {});
    };

    const openRoleModal = () => {
        setDraftRole(staff?.role ?? '');
        setRoleModalOpen(true);
    };

    const handleRoleSave = async () => {
        if (!id || !draftRole || draftRole === staff?.role) {
            setRoleModalOpen(false);
            return;
        }
        setSavingRole(true);
        try {
            await api.patch(`/api/staff/${id}/role`, { role: draftRole });
            setStaff((prev) => (prev ? { ...prev, role: draftRole as StaffMember['role'] } : prev));
            toast.success('Role updated');
            setRoleModalOpen(false);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to update role');
        } finally {
            setSavingRole(false);
        }
    };

    const isCurrentlyBlocked = staff?.status === 'blocked';

    const initials =
        staff?.name
            ?.split(' ')
            .map((w) => w[0])
            .join('')
            .slice(0, 2)
            .toUpperCase() ?? '??';

    const statsCards = [
        { label: 'All Cases Managed', value: String(stats?.total ?? 0) },
        { label: 'New', value: String(stats?.New ?? 0) },
        { label: 'Assigned', value: String(stats?.Assigned ?? 0) },
        { label: 'In Progress', value: String(stats?.['In Progress'] ?? 0) },
        { label: 'Resolved', value: String(stats?.Resolved ?? 0) },
    ];

    const COLUMNS: Column<CaseRow>[] = [
        { key: 'sn', label: 'S/N' },
        { key: 'date', label: 'Date' },
        { key: 'complainantName', label: 'Complainant Name' },
        { key: 'caseCategory', label: 'Case Category' },
        { key: 'disputeCategory', label: 'Dispute Category' },
        { key: 'amountPaid', label: 'Amount Paid' },
        {
            key: 'status',
            label: 'Case Status',
            render: (row) => <StatusBadge status={row.status} />,
        },
        {
            key: 'actions',
            label: 'Actions',
            render: (row) => <ViewButton onClick={() => setSelectedCase(row)} />,
        },
    ];

    return (
        <div className="space-y-6  py-6">
            <div className="flex flex-col gap-4 px-4 lg:flex-row lg:items-start lg:gap-6 lg:px-8">
                <div className="flex items-start gap-3">
                    <button
                        type="button"
                        title="Back to Internal Users"
                        onClick={() => navigate('/dashboard/users')}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#eee] text-[#555] transition-colors hover:bg-[#f5f5f5]"
                    >
                        <MdChevronLeft size={20} />
                    </button>

                    <div className="rounded-xl border border-[#eee] px-5 py-3">
                        <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#e8e8e8] text-sm font-bold text-secondary">
                                {initials}
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <p className="text-[15px] font-bold text-secondary">{staff?.name ?? '…'}</p>
                                    {staff?.status === 'active' && (
                                        <span className="flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-[12px] font-medium text-green-600">
                                            <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                                            Active
                                        </span>
                                    )}
                                    {staff?.status === 'blocked' && (
                                        <span className="rounded-full bg-red-50 px-2 py-0.5 text-[12px] font-medium text-red-600">
                                            Blocked
                                        </span>
                                    )}
                                    {staff?.role && (
                                        <span className="rounded-full bg-[#FFF4EE] px-2 py-0.5 text-[12px] font-medium text-primary">
                                            {staff.role}
                                        </span>
                                    )}
                                </div>
                                <p className="text-[13px] text-[#888]">{staff?.email ?? ''}</p>
                            </div>
                        </div>
                        <div className="mt-2 flex items-center justify-between border-t border-[#eee] pt-2">
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={() => setBlockModalOpen(true)}
                                    className="flex items-center gap-1 text-[13px] font-medium text-red-500 transition-colors hover:text-red-700"
                                >
                                    <MdClose size={14} />
                                    {isCurrentlyBlocked ? 'Unblock' : 'Block'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setResendModalOpen(true)}
                                    className="flex items-center gap-1 text-[13px] font-medium text-[#555] transition-colors hover:text-secondary"
                                >
                                    <MdOutlineRefresh size={14} />
                                    Resend Invitation
                                </button>
                                <button
                                    type="button"
                                    onClick={openRoleModal}
                                    className="flex items-center gap-1 text-[13px] font-medium text-[#555] transition-colors hover:text-secondary"
                                >
                                    <MdOutlineEdit size={14} />
                                    Change Role
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid flex-1 grid-cols-3 gap-3 sm:grid-cols-5">
                    {statsCards.map((s) => (
                        <StatsCard key={s.label} stat={s} />
                    ))}
                </div>
            </div>

            <DataTable
                title="Cases"
                columns={COLUMNS}
                data={cases}
                getRowKey={(row) => row.id}
                tabs={TABS}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                onExportCSV={() =>
                    exportCSV(`staff-${staff?.name ?? id}-cases`, COLUMNS, cases)
                }
            />

            <CaseViewModal open={!!selectedCase} onClose={() => setSelectedCase(null)} caseData={selectedCase} />

            <ConfirmActionModal
                open={blockModalOpen}
                onClose={() => setBlockModalOpen(false)}
                title={isCurrentlyBlocked ? 'Unblock this user?' : 'Block this user?'}
                description={
                    isCurrentlyBlocked
                        ? `${staff?.name ?? 'This user'} will regain access to the dashboard and any cases assigned to them.`
                        : `${staff?.name ?? 'This user'} will lose access to the dashboard until they are unblocked.`
                }
                confirmLabel={isCurrentlyBlocked ? 'Yes, Unblock' : 'Yes, Block'}
                variant={isCurrentlyBlocked ? 'primary' : 'danger'}
                successTitle={isCurrentlyBlocked ? 'User Unblocked' : 'User Blocked'}
                successDescription={
                    isCurrentlyBlocked
                        ? 'The user has been unblocked and can sign in again.'
                        : 'The user has been blocked. They can no longer sign in until you unblock them.'
                }
                onConfirm={handleBlockConfirm}
            />

            <ConfirmActionModal
                open={resendModalOpen}
                onClose={() => setResendModalOpen(false)}
                title="Resend Invitation?"
                description={`A new invitation email with login credentials will be sent to ${staff?.email ?? 'this user'}.`}
                confirmLabel="Yes, Resend"
                successTitle="Invitation Sent"
                successDescription="The invitation email has been resent with fresh login credentials."
                onConfirm={handleResendConfirm}
            />

            <SlideModal open={roleModalOpen} onClose={() => setRoleModalOpen(false)}>
                <div className="flex flex-1 flex-col px-8 pb-6">
                    <div className="pt-2">
                        <h2 className="text-xl font-bold text-secondary">Change User Role</h2>
                        <p className="mt-2 text-[14px] leading-relaxed text-[#888]">
                            Update the role for {staff?.name ?? 'this user'}. They will gain or lose dashboard access
                            based on the new role.
                        </p>

                        <div className="mt-6">
                            <label className="mb-1.5 block text-[13px] font-medium text-secondary">Role</label>
                            <FormSelect
                                value={draftRole}
                                onChange={setDraftRole}
                                options={ROLE_OPTIONS}
                                placeholder="Select a role"
                            />
                        </div>
                    </div>

                    <div className="mt-auto flex gap-3">
                        <button
                            type="button"
                            onClick={() => setRoleModalOpen(false)}
                            disabled={savingRole}
                            className="flex flex-1 items-center justify-center rounded-xl border border-[#E0E0E0] bg-white py-4 text-[15px] font-bold text-secondary transition-all hover:bg-[#f5f5f5] disabled:opacity-60"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleRoleSave}
                            disabled={savingRole || !draftRole || draftRole === staff?.role}
                            className="flex flex-1 items-center justify-center rounded-xl bg-secondary py-4 text-[15px] font-bold text-white transition-all hover:bg-secondary-400 disabled:opacity-60"
                        >
                            {savingRole ? 'Saving…' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </SlideModal>
        </div>
    );
}
